import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Radio, Row } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorResult, SketchPicker } from 'react-color';
import { isEmpty } from 'lodash';
import { THE_DAILY } from '../../../../constants/define/translation';
import {
  patchUpdateTileWidgetRepository,
  postCreateTileWidgetRepository,
} from '../../../../repository/mytm/shop/shopRepository';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { LinkType, LinkTypeList, SHOP_TYPE, ShopStatus, ShopType } from '../../../../types/shop';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../../atoms';
import { PreviewTheDaily } from '../../../molecules/preview/common';
import { DragDropImageVideo } from '../../../molecules/upload';
import { CountryContext } from '../../../../repository/context/countryContext';
import { APP_DEEP_LINK_FEATURES_V2 } from '../../../../constants/home';
import { getFuLLUrlEcom, URLType, URLTypeList } from '../../../../constants/common';

interface FormTheDailyProps {
  record?: ShopType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  countrySelected: string;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const FormTheDaily = (props: FormTheDailyProps) => {
  const { t } = useTranslation(THE_DAILY);
  const { record, onRefresh, onCloseModal, setDataRecord, countrySelected } = props;
  const [form] = Form.useForm();
  const options = record?.options ? JSON.parse(record.options) : null;
  const defaultValue = {
    title: record?.title,
    subTitle: record?.description,
    type: SHOP_TYPE.PRODUCT_TILES,
    imageLink: record?.imageLink,
    videoLink: record?.videoLink,
    ctaText: record?.ctaText || 'Watch Now',
    ctaLink: record?.ctaLink || '',
    status: record?.status || ShopStatus.INACTIVE,
    countries: record ? record?.countries : countrySelected || 'USA',
    titleColor: options?.titleColor || '#fff',
    ctaTextColor: options?.ctaTextColor || '#000',
    ctaBackgroundColor: options?.ctaBackgroundColor || '#fff',
    category: options?.category || LinkType.WEB_LINK,
    ctaCMS: options?.ctaCMS || null,
    cmsType: options?.cmsType || null,
    cmsFormat: options?.cmsFormat || null,
    ctaDeepLink: options?.ctaDeepLink || null,
    ctaLinkType: options?.ctaLinkType || URLType.PDP,
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [videoList, setVideoList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);
  const [isDeepLink, setIsDeepLink] = useState(false);
  const [isBlockCtaLink, setBlockCtaLink] = useState(false);
  const [isCMS, setIsCMS] = useState(false);
  const countryContext = useContext(CountryContext);
  const [isShowColorPicker, setIsShowColorPicker] = useState<any>({
    isBackGroundColor: false,
    isTitleColor: false,
    isSubColor: false,
    isCTATextColor: false,
    isCTABackgroundColor: false,
  });

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  const DeepLinkList = APP_DEEP_LINK_FEATURES_V2.map(val => {
    return {
      text: val?.value,
      value: val?.ctaLink,
    };
  });

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSetVideoList = useCallback((data: UploadFileToBlobInterface[]) => {
    setVideoList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onChangeInput = useCallback(
    e => {
      const newField = form.getFieldsValue();
      let ctaNewLink = newField?.ctaLink || '';
      if (newField && newField.category === LinkType.DEEP_LINK) {
        setIsDeepLink(true);
        setIsCMS(false);
      } else if (newField && newField.category === LinkType.CMS) {
        setIsCMS(true);
        setIsDeepLink(false);
      } else {
        setIsCMS(false);
        setIsDeepLink(false);
      }

      if (
        newField &&
        newField.ctaLinkType &&
        ![URLType.PDP, URLType.CUSTOM, URLType.GRID_PAGE].includes(newField.ctaLinkType)
      ) {
        const dataUrl = getFuLLUrlEcom(newField?.ctaLinkType, newField?.countries);
        ctaNewLink = dataUrl?.ctaNewLink;
        setBlockCtaLink(dataUrl?.blockLink);

        form.setFieldsValue({
          ...newField,
          ctaLink: ctaNewLink,
        });
      } else {
        setBlockCtaLink(false);
      }
      setDataForm((old: any) => ({
        ...old,
        title: newField?.title,
        category: newField?.category,
        ctaLink: ctaNewLink,
        ctaText: newField?.ctaText,
        ctaDeepLink: newField?.ctaDeepLink,
        ctaCMS: newField?.ctaCMS,
        cmsType: newField?.cmsType,
        cmsFormat: newField?.cmsFormat,
        ctaLinkType: newField?.ctaLinkType,
      }));
    },
    [form],
  );

  const onShowColorPicker = useCallback((fieldName: string, value) => {
    setIsShowColorPicker({
      ...isShowColorPicker,
      [fieldName]: value,
    });
  }, []);

  const onCloseColorPicker = useCallback(
    (fieldName: string) => {
      setIsShowColorPicker({
        ...isShowColorPicker,
        [fieldName]: false,
      });
    },
    [isShowColorPicker],
  );

  const handleChangeColor = useCallback(
    (value: ColorResult, fieldName: string) => {
      setDataForm((old: any) => ({
        ...old,
        [fieldName]: value?.hex,
      }));
    },
    [dataForm],
  );

  const onFinish = useCallback(
    (formValue: any) => {
      if (!(imageList.length || videoList.length)) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      let imgList = imageList.filter(img => !img.isImageURL);
      let imgLink = imageList.filter(img => img.isImageURL);
      if (isEmpty(imgList)) {
        imgList = videoList.filter(img => !img.isVideoURL);
      }

      if (isEmpty(imgLink)) {
        imgLink = videoList.filter(img => img.isVideoURL);
      }

      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const videoType = !!videoList[0]?.fileType?.includes('video');
          const url = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];

          const countryStr = form.getFieldValue('countries').toString();
          const newOptions = {
            titleColor: dataForm?.titleColor,
            ctaTextColor: dataForm?.ctaTextColor,
            ctaBackgroundColor: dataForm?.ctaBackgroundColor,
            category: dataForm?.category,
            ctaCMS: dataForm?.ctaCMS,
            cmsType: dataForm?.cmsType,
            cmsFormat: dataForm?.cmsFormat,
            ctaDeepLink: dataForm?.ctaDeepLink,
            ctaLinkType: dataForm?.ctaLinkType,
          };
          const payloadToPost = {
            title: dataForm?.title,
            type: SHOP_TYPE.THE_DAILY,
            imageLink: (!videoType && url) || null,
            videoLink: (videoType && url) || null,
            ctaText: dataForm?.ctaText,
            ctaLink: dataForm?.ctaLink,
            options: newOptions,
            countries: countryStr,
            status: dataForm?.status,
          };
          if (record) {
            return patchUpdateTileWidgetRepository({
              ...payloadToPost,
              status: record?.status,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onCloseModal();
                message.success(t('message.success.updateTheDaily'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreateTileWidgetRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createTheDaily'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList, dataForm, videoList],
  );

  useEffect(() => {
    if (record) {
      setImageList(record?.imageLink ? [{ dataURL: record?.imageLink, isImageURL: true }] : []);
      setVideoList(
        record?.videoLink ? [{ dataURL: record?.videoLink || null, isVideoURL: true, fileType: 'video' }] : [],
      );
      setSelectedImage(0);
      setDataForm({
        ...defaultValue,
        countries: record?.countries,
      });
      if (options.category && options.category === LinkType.CMS) {
        setIsCMS(true);
        setIsDeepLink(false);
      } else if (options.category && options.category === LinkType.DEEP_LINK) {
        setIsDeepLink(true);
        setIsCMS(false);
      } else {
        setIsCMS(false);
        setIsDeepLink(false);
      }

      if (options && options.ctaLinkType) {
        if (
          options.ctaLinkType === URLType.PDP ||
          options.ctaLinkType === URLType.CUSTOM ||
          options.ctaLinkType === URLType.GRID_PAGE
        ) {
          setBlockCtaLink(false);
        } else {
          setBlockCtaLink(true);
        }
      }
    }
  }, [record]);

  useEffect(() => {
    if (imageList.length || videoList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList, videoList]);

  const renderLinkCta = () => {
    if (isDeepLink) {
      return (
        <Row>
          <Col span={24}>
            <SelectOption
              modeForm
              name="ctaDeepLink"
              label={t('form.ctaDeepLink')}
              rules={[{ required: true, message: t('validate.ctaDeepLinkBlank') }]}
              values={DeepLinkList}
              textDisplay="text"
              onChange={onChangeInput}
              showSearch
              allowClear
            />
          </Col>
        </Row>
      );
    }

    if (isCMS) {
      return (
        <Row gutter={[12, 12]}>
          <Col span={8}>
            <Form.Item
              name="ctaCMS"
              label={t('form.ctaCMS')}
              rules={[{ required: true, message: t('validate.ctaCMSBlank') }]}
            >
              <Input placeholder={t('form.ctaCMS')} maxLength={255} onChange={onChangeInput} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="cmsType"
              label={t('form.contentType')}
              rules={[{ required: true, message: t('validate.contentTypeBlank') }]}
            >
              <Input placeholder={t('form.contentType')} maxLength={255} onChange={onChangeInput} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="cmsFormat"
              label={t('form.contentFormat')}
              rules={[{ required: true, message: t('validate.contentFormatBlank') }]}
            >
              <Input placeholder={t('form.contentFormat')} maxLength={255} onChange={onChangeInput} />
            </Form.Item>
          </Col>
        </Row>
      );
    }
    return (
      <Row>
        <Col span={24}>
          <SelectOption
            modeForm
            name="ctaLinkType"
            label={t('form.linkType')}
            rules={[{ required: true, message: t('validate.linkTypeBlank') }]}
            values={URLTypeList}
            textDisplay="text"
            onChange={onChangeInput}
            showSearch
            allowClear
          />
        </Col>
        <Col span={24}>
          <Form.Item
            name="ctaLink"
            label={t('form.link')}
            rules={[{ required: true, message: t('validate.linkBlank') }]}
          >
            <Input disabled={isBlockCtaLink} placeholder={t('form.link')} maxLength={255} onChange={onChangeInput} />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-TheDaily">
            <Row className="form-TheDaily-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImageVideo
                  {...{
                    imageList,
                    onSetImageList,
                    setMessageErrorUploadImage,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    videoList,
                    onSetVideoList,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewTheDaily
                        values={{
                          ...dataForm,
                          backgroundImage: (imageList?.length && imageList[0].dataURL) || null,
                          backgroundVideo: (videoList?.length && videoList[0].dataURL) || null,
                          describeText: t('form.imageUpload'),
                          describeText2: t('form.image3x4'),
                        }}
                        className="preview-the-daily"
                        height="400px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormTheDaily span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="countries"
                        label={t('form.countries')}
                        rules={[{ required: true, message: t('validate.countryBlank') }]}
                      >
                        <Radio.Group options={countryOptions} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <SelectOption
                        modeForm
                        name="category"
                        label={t('form.category')}
                        rules={[{ required: true, message: t('validate.categoryBlank') }]}
                        values={LinkTypeList}
                        textDisplay="text"
                        onChange={onChangeInput}
                        showSearch
                        allowClear
                      />
                    </Col>
                  </Row>
                  {renderLinkCta()}

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="ctaText"
                        label={t('form.cta')}
                        rules={[{ required: true, message: t('validate.ctaBlank') }]}
                      >
                        <Input placeholder={t('form.cta')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    <Col span={8}>
                      <Form.Item label={t('form.titleColor')} name="titleColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isTitleColor', !isShowColorPicker.isTitleColor);
                          }}
                        />
                        {isShowColorPicker.isTitleColor && (
                          <div className="popover">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isTitleColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isTitleColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.titleColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'titleColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={t('form.ctaTextColor')} name="ctaTextColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isCTATextColor', !isShowColorPicker.isCTATextColor);
                          }}
                        />
                        {isShowColorPicker.isCTATextColor && (
                          <div className="popover">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isCTATextColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isCTATextColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.ctaTextColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'ctaTextColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={t('form.ctaBackgroundColor')} name="ctaBackgroundColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isCTABackgroundColor', !isShowColorPicker.isCTABackgroundColor);
                          }}
                        />
                        {isShowColorPicker.isCTABackgroundColor && (
                          <div className="popoverSub">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isCTABackgroundColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isCTABackgroundColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.ctaBackgroundColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'ctaBackgroundColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end' }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdateTheDaily') : t('form.btnCreateTheDaily')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormTheDaily>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-TheDaily-container {
    padding: 1.5rem;

    .form-TheDaily__description {
      resize: none;
    }
  }

  & .preview-the-daily {
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
    bottom: -15px !important;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormTheDaily = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }
`;
